import React from "react"
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"

// =============================================================================
// Props
// =============================================================================

export type TooltipProviderProps = {
  /** Tooltip content */
  children?: React.ReactNode
  /** How long to wait before opening a tooltip (ms) */
  delay?: number
  /** How long to wait before closing a tooltip (ms) */
  closeDelay?: number
  /** Adjacent tooltip opens instantly if previous closed within this timeout (ms) */
  timeout?: number
}

export type TooltipRootProps = {
  /** The tooltip trigger and content */
  children?: React.ReactNode
  /** Whether the tooltip is initially open */
  defaultOpen?: boolean
  /** Controlled open state */
  open?: boolean
  /** Called when the tooltip is opened or closed */
  onOpenChange?: (open: boolean) => void
  /** Called after any open/close animations complete */
  onOpenChangeComplete?: (open: boolean) => void
  /** Whether the tooltip is disabled */
  disabled?: boolean
}

export type TooltipTriggerProps = {
  /** The trigger content */
  children?: React.ReactNode
  /** Replace the default button element with your own */
  render?: React.ReactElement
  /** Per-trigger delay override (ms) */
  delay?: number
  /** Per-trigger close delay override (ms) */
  closeDelay?: number
  /** Whether the tooltip should close when this trigger is clicked */
  closeOnClick?: boolean
  /** Disables the trigger without applying the disabled attribute */
  disabled?: boolean
  /** Additional CSS classes for the trigger element */
  className?: string
}

export type TooltipContentProps = {
  /** Which side of the trigger the tooltip appears on */
  position?: "top" | "bottom" | "left" | "right"
  /** Alignment of the tooltip relative to the trigger */
  align?: "start" | "center" | "end"
  /** Whether to show the arrow pointing to the trigger */
  showArrow?: boolean
  /** Content displayed inside the tooltip */
  children: React.ReactNode
}

// =============================================================================
// Components
// =============================================================================

function TooltipProvider({
  delay = 200,
  children,
  closeDelay,
  timeout
}: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      closeDelay={closeDelay}
      timeout={timeout}
    >
      {children}
    </TooltipPrimitive.Provider>
  )
}

function TooltipRoot({
  children,
  defaultOpen,
  open,
  onOpenChange,
  onOpenChangeComplete,
  disabled
}: TooltipRootProps) {
  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      disabled={disabled}
    >
      {children}
    </TooltipPrimitive.Root>
  )
}

function TooltipTrigger({
  children,
  render,
  delay,
  closeDelay,
  closeOnClick,
  disabled,
  className
}: TooltipTriggerProps) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      render={render}
      delay={delay}
      closeDelay={closeDelay}
      closeOnClick={closeOnClick}
      disabled={disabled}
      className={className}
    >
      {children}
    </TooltipPrimitive.Trigger>
  )
}

const styles = {
  popup: [
    // layout & positioning
    "inline-flex w-fit max-w-lg origin-(--transform-origin) items-center gap-2",
    // appearance
    "rounded-[10px] bg-black px-4 py-2 text-sm text-white ring-1 ring-inset ring-gray-700",
    // slide direction per side
    "data-[side=top]:slide-in-from-bottom-[0.5rem] data-[side=bottom]:slide-in-from-top-[0.5rem]",
    "data-[side=left]:slide-in-from-right-[0.5rem] data-[side=right]:slide-in-from-left-[0.5rem]",
    // open animation
    "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
    "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
    // close animation
    "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
  ].join(" "),
  arrow: [
    "size-3 rotate-45 rounded-[2px] bg-black fill-black border-gray-700",
    // top: arrow below popup, push to bottom edge, clear inline top
    "data-[side=top]:top-auto! data-[side=top]:bottom-0! data-[side=top]:translate-y-[50%]! data-[side=top]:border-b data-[side=top]:border-r",
    // bottom: arrow above popup, push to top edge, clear inline top
    "data-[side=bottom]:top-0! data-[side=bottom]:-translate-y-[50%]! data-[side=bottom]:border-t data-[side=bottom]:border-l",
    // left: arrow to the right, push to right edge, clear inline left
    "data-[side=left]:left-auto! data-[side=left]:right-0! data-[side=left]:translate-x-[50%]! data-[side=left]:border-t data-[side=left]:border-r",
    // right: arrow to the left, push to left edge, clear inline left
    "data-[side=right]:left-0! data-[side=right]:-translate-x-[50%]! data-[side=right]:border-b data-[side=right]:border-l"
  ].join(" ")
}

function TooltipContent({
  position = "top",
  align = "center",
  showArrow = false,
  children,
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        sideOffset={8}
        side={position}
        align={align}
        className="isolate z-[5001]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={styles.popup}
        >
          {children}
          {showArrow && <TooltipPrimitive.Arrow className={styles.arrow} />}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

// =============================================================================
// Compound component
// =============================================================================

interface TooltipComponent {
  (props: TooltipRootProps): React.JSX.Element
  Provider: React.ComponentType<TooltipProviderProps>
  Trigger: React.ComponentType<TooltipTriggerProps>
  Content: React.ComponentType<TooltipContentProps>
}

TooltipRoot.displayName = "Tooltip"
TooltipProvider.displayName = "Tooltip.Provider"
TooltipTrigger.displayName = "Tooltip.Trigger"
TooltipContent.displayName = "Tooltip.Content"

export const Tooltip: TooltipComponent = Object.assign(TooltipRoot, {
  Provider: TooltipProvider,
  Trigger: TooltipTrigger,
  Content: TooltipContent
} as const)
